import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/client';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Input, { Select, Label } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { formatDateTime } from '../../lib/dateUtils';
import { ChevronLeft, ChevronRight, Eye, Search } from 'lucide-react';

const getActionColor = (action) => {
  switch (action) {
    case 'create':
      return 'bg-green-100 text-green-800';
    case 'update':
      return 'bg-blue-100 text-blue-800';
    case 'delete':
      return 'bg-red-100 text-red-800';
    case 'login':
      return 'bg-purple-100 text-purple-800';
    case 'logout':
      return 'bg-gray-100 text-gray-800';
    case 'export':
      return 'bg-yellow-100 text-yellow-800';
    case 'line_send':
      return 'bg-teal-100 text-teal-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function AuditLogsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [filters, setFilters] = useState({
    action: '',
    tableName: '',
    page: 1,
    limit: 50,
  });

  const [detailsModal, setDetailsModal] = useState({ open: false, log: null });

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: async () => {
      const res = await api.get('/api/admin/audit-logs', { params: filters });
      return res.data;
    },
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const logs = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{t('nav.auditLogs')}</h1>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Action</Label>
              <Select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">{t('common.all')}</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="export">Export</option>
                <option value="line_send">LINE Send</option>
              </Select>
            </div>

            <div>
              <Label>Table</Label>
              <Select
                value={filters.tableName}
                onChange={(e) => handleFilterChange('tableName', e.target.value)}
              >
                <option value="">{t('common.all')}</option>
                <option value="users">Users</option>
                <option value="reports">Reports</option>
                <option value="work_items">Work Items</option>
                <option value="contractors">Contractors</option>
                <option value="inspectors">Inspectors</option>
                <option value="announcements">Announcements</option>
                <option value="holidays">Holidays</option>
                <option value="app_settings">Settings</option>
                <option value="daily_summaries">Daily Summaries (LINE)</option>
              </Select>
            </div>

            <div className="flex items-end">
              <Button variant="outline" className="w-full">
                <Search className="w-4 h-4 mr-2" />
                {t('common.search')}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardBody className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Table</TableHead>
                <TableHead>Record ID</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      {formatDateTime(log.createdAt, locale)}
                    </TableCell>
                    <TableCell>{log.username}</TableCell>
                    <TableCell>
                      <Badge className={getActionColor(log.action)}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell>{log.tableName || '-'}</TableCell>
                    <TableCell>{log.recordId || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{log.ipAddress || '-'}</TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDetailsModal({ open: true, log })}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-12">
                    {t('common.noData')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {pagination && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {t('common.page')} {pagination.page} {t('common.of')} {Math.max(pagination.totalPages, 1)}
                {' '}({pagination.total} {t('common.items')})
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('common.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  {t('common.next')}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Details Modal */}
      <Modal
        isOpen={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, log: null })}
        title="Audit Log Details"
        size="lg"
      >
        {detailsModal.log && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Time</p>
                <p>{formatDateTime(detailsModal.log.createdAt, locale)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">User</p>
                <p>{detailsModal.log.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Action</p>
                <Badge className={getActionColor(detailsModal.log.action)}>
                  {detailsModal.log.action}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Table</p>
                <p>{detailsModal.log.tableName || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Record ID</p>
                <p>{detailsModal.log.recordId || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">IP Address</p>
                <p className="font-mono">{detailsModal.log.ipAddress || '-'}</p>
              </div>
            </div>

            {detailsModal.log.details && (
              <div>
                <p className="text-sm text-gray-500 mb-2">Details</p>
                <pre className="bg-gray-100 p-4 rounded-lg text-sm font-mono overflow-auto max-h-64">
                  {JSON.stringify(JSON.parse(detailsModal.log.details), null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
